import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  createGhostSubscriptionCustom,
  getStripeSubscriptions,
} from "../../apiCalls/caller";
import logoLight from "../../assets/images/buyers-club.png";
import axios from "axios";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const CheckoutForm = ({ priceId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      name: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email"),
      name: Yup.string().required("Please enter your full name"),
    }),
    onSubmit: async (values) => {
      if (!stripe || !elements) {
        setError("Stripe is not ready. Please refresh the page.");
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        // Submit the payment element
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message);
          setIsProcessing(false);
          return;
        }

        // Confirm the setup intent
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href, // Not used since we use redirect: 'if_required'
            payment_method_data: {
              billing_details: {
                email: values.email,
                name: values.name,
                phone: ''
              },
            },
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message);
          setIsProcessing(false);
          return;
        }

        // Call backend to complete subscription creation
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}users/CompleteGhostSubscription`,
          {
            email: values.email,
            name: values.name,
            setupIntentId: setupIntent.id,
            priceId: priceId,
          }
        );

        if (response.data.success) {
          setShowSuccess(true);
          // Redirect to password set page
          setTimeout(() => {
            window.location.href = response.data.redirectUrl;
          }, 1500);
        } else {
          setError(response.data.error || "Failed to complete subscription");
          setIsProcessing(false);
        }
      } catch (err) {
        console.error("Subscription error:", err);
        setError(err.response?.data?.error || "An unexpected error occurred. Please try again.");
        setIsProcessing(false);
      }
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      {error && (
        <Alert color="danger" className="mb-3">
          <i className="ri-error-warning-line me-2"></i>
          {error}
        </Alert>
      )}

      {showSuccess && (
        <Alert color="success" className="mb-3">
          <i className="ri-checkbox-circle-line me-2"></i>
          Subscription created successfully! Redirecting...
        </Alert>
      )}

      <div className="mb-3">
        <Label htmlFor="email" className="form-label">
          Email <span className="text-danger">*</span>
        </Label>
        <Input
          name="email"
          type="email"
          placeholder="Enter your email"
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.email || ""}
          invalid={
            validation.touched.email && validation.errors.email ? true : false
          }
          disabled={isProcessing || showSuccess}
        />
        {validation.touched.email && validation.errors.email ? (
          <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
        ) : null}
      </div>

      <div className="mb-3">
        <Label htmlFor="name" className="form-label">
          Full Name <span className="text-danger">*</span>
        </Label>
        <Input
          name="name"
          type="text"
          placeholder="Enter your full name"
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.name || ""}
          invalid={
            validation.touched.name && validation.errors.name ? true : false
          }
          disabled={isProcessing || showSuccess}
        />
        {validation.touched.name && validation.errors.name ? (
          <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
        ) : null}
      </div>

      <div className="mb-4">
        <Label className="form-label">
          Payment Information <span className="text-danger">*</span>
        </Label>
        <PaymentElement options={{
          fields: {
            billingDetails: {
              name: 'never',
              email: 'never',
              phone: 'never'
            }
          },
          
        }} />
      </div>

      <div className="mt-4">
        <Button
          color="primary"
          className="btn w-100"
          type="submit"
          disabled={isProcessing || showSuccess || !stripe || !elements}
        >
          {isProcessing || showSuccess ? (
            <>
              <Spinner size="sm" className="me-2">
                Loading...
              </Spinner>
              {showSuccess ? "Redirecting..." : "Processing..."}
            </>
          ) : (
            "Complete Subscription"
          )}
        </Button>
      </div>
    </Form>
  );
};

const Register = () => {
  const location = useLocation();
  const frequency = location.pathname === "/monthly" ? "monthly" : "yearly";
  const [clientSecret, setClientSecret] = useState(null);
  const [priceId, setPriceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        // Fetch client secret and subscription data in parallel
        const [secretResponse, subsResponse] = await Promise.all([
          createGhostSubscriptionCustom(frequency),
          getStripeSubscriptions("", frequency),
        ]);

        if (secretResponse?.clientSecret) {
          // const decodedSecret = decodeURIComponent(secretResponse.clientSecret);
          // Remove any trailing data after the first forward slash
          // const cleanSecret = decodedSecret.split('/')[0];
          setClientSecret(secretResponse.clientSecret);
          setPriceId(secretResponse.priceId);
        } else {
          setError("Failed to initialize checkout session.");
        }

        if (subsResponse && subsResponse.length > 0) {
          setSubscriptionData(subsResponse[0]);
        }
      } catch (err) {
        console.error("Error initializing checkout:", err);
        setError(
          "Unable to load checkout. Please refresh the page or try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    initializeCheckout();
  }, [frequency]);

  const formatPrice = (priceInCents, interval) => {
    if (!priceInCents) return "";
    const dollars = (priceInCents / 100).toFixed(0);
    return `$${dollars}/${interval}`;
  };

  document.title = `SignUp | ${process.env.REACT_APP_SITE_TITLE}`;

  if (loading) {
    return (
      <div className="auth-page-content">
        <Container>
          <Row
            className="justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
          >
            <Col xs={12} className="text-center">
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-3 text-muted">Loading checkout...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center" style={{ minHeight: "100vh", alignItems: "center" }}>
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <Alert color="danger">
                    <h5 className="alert-heading">Error</h5>
                    <p>{error}</p>
                    <hr />
                    <Button
                      color="danger"
                      outline
                      onClick={() => window.location.reload()}
                    >
                      Refresh Page
                    </Button>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={10} xl={9}>
              <div className="text-center mt-3 mt-sm-4 mt-md-5 mb-2 mb-md-4">
                <img src={logoLight} alt="Buyers Club" className="register-logo" />
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={10} lg={10} xl={9}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <Row>
                    {/* Left Column - Subscription Details */}
                    <Col lg={5} className="">
                      <div className="p-3">
                        <h4 className="text-primary mb-3">
                          Buyers Club Membership
                        </h4>
                        
                        {subscriptionData && (
                          <div className="mb-4">
                            <h2 className="text-primary">
                              {formatPrice(
                                subscriptionData.monthly || subscriptionData.annual,
                                subscriptionData.monthly ? "month" : "year"
                              )}
                            </h2>
                          </div>
                        )}

                        <div className="mb-3">
                          <h6 className="mb-3">Membership Includes:</h6>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              Full time staff of Buyers and Product Analysts
                            </li>
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              Access to thousands of curated products that are updated daily
                            </li>
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              No need to set up supplier accounts, we do the heavy lifting!
                            </li>
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              Integration with Sellercloud ($1200 value) which allows for inventory management and fulfillment across 100's of sales channels.
                            </li>
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              Automated process from product ordering to end customer fulfillment
                            </li>
                            <li className="mb-2">
                              <i className="ri-check-line text-success me-2"></i>
                              Weekly HOT list items that are fast selling!
                            </li>
                          </ul>
                        </div>
                      </div>
                    </Col>

                    {/* Right Column - Checkout Form */}
                    <Col lg={7}>
                      <div className="p-3">
                        <div className="text-center mb-4">
                          <h5 className="text-primary">Complete Your Subscription</h5>
                          <p className="text-muted">
                            Enter your details to get started
                          </p>
                        </div>

                        {clientSecret && (
                          <Elements
                            stripe={stripePromise}
                            options={{ 
                              clientSecret,
                              appearance: {
                                theme: 'stripe'
                              }
                            }}
                          >
                            <CheckoutForm priceId={priceId} />
                          </Elements>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
